import { loadLocalImage } from './image-loader.ts';

const MAX_EDGE = 1280;
const MAX_BYTES = 4 * 1024 * 1024;
const encoder = new TextEncoder();
function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (crc & 1 ? 0xedb88320 : 0);
  }
  return (crc ^ 0xffffffff) >>> 0;
}
function chunk(name: string, data: Uint8Array): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(data.length + 12);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, data.length);
  bytes.set(encoder.encode(name), 4);
  bytes.set(data, 8);
  view.setUint32(bytes.length - 4, crc32(bytes.subarray(4, bytes.length - 4)));
  return bytes;
}
/** Exact RGB8/filter0 PNG grammar shared with the server; no source metadata survives. */
export async function encodeBackgroundPng(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
): Promise<Blob> {
  if (
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    width > MAX_EDGE ||
    height > MAX_EDGE ||
    rgba.length !== width * height * 4
  )
    throw new Error('image processing failed');
  const rows = new Uint8Array(height * (1 + width * 3));
  for (let y = 0; y < height; y++)
    for (let x = 0; x < width; x++) {
      const source = (y * width + x) * 4;
      const destination = y * (1 + width * 3) + 1 + x * 3;
      const alpha = rgba[source + 3] ?? 0;
      for (let channel = 0; channel < 3; channel++)
        rows[destination + channel] = Math.round(
          ((rgba[source + channel] ?? 0) * alpha + 255 * (255 - alpha)) / 255,
        );
    }
  if (typeof CompressionStream === 'undefined') throw new Error('image processing unavailable');
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  let compressed: Uint8Array<ArrayBuffer>;
  try {
    compressed = new Uint8Array(
      await new Response(
        new Blob([rows])
          .stream()
          .pipeThrough(new CompressionStream('deflate'), { signal: controller.signal }),
      ).arrayBuffer(),
    );
  } finally {
    clearTimeout(timer);
  }
  const header = new Uint8Array(13);
  const view = new DataView(header.buffer);
  view.setUint32(0, width);
  view.setUint32(4, height);
  header.set([8, 2, 0, 0, 0], 8);
  const result = new Blob(
    [
      new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]),
      chunk('IHDR', header),
      chunk('IDAT', compressed),
      chunk('IEND', new Uint8Array()),
    ],
    { type: 'image/png' },
  );
  if (result.size > MAX_BYTES) throw new Error('image processing failed');
  return result;
}
export async function prepareBackgroundImage(blob: Blob): Promise<Blob> {
  const image = await loadLocalImage(blob);
  const { naturalWidth: width, naturalHeight: height } = image;
  if (width > 8192 || height > 8192 || width * height > 16_000_000)
    throw new Error('image processing failed');
  const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new Error('image processing unavailable');
  context.fillStyle = '#fff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return encodeBackgroundPng(
    context.getImageData(0, 0, canvas.width, canvas.height).data,
    canvas.width,
    canvas.height,
  );
}
